import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["choice", "all", "bar", "selectedCount", "selectedTotals", "dialog", "summary", "totals", "skipped", "send", "feedback"]
  static values = { previewUrl: String, payUrl: String, filters: Object }
  connect() {
    this.busy = false
    this.abort = new AbortController()
    this.dialogTarget.addEventListener("cancel", event => { if (this.busy) event.preventDefault() }, {signal: this.abort.signal})
    try { const message = sessionStorage.getItem("paypal-payout-result"); if (message) { this.message(message); sessionStorage.removeItem("paypal-payout-result") } } catch (_) {}
    this.selection()
  }
  disconnect() { this.abort.abort(); clearTimeout(this.timer) }
  money(amount, currency) { return new Intl.NumberFormat("en-CA", {style: "currency", currency, currencyDisplay: "code"}).format(amount / 100) }
  selected() { return this.choiceTargets.filter(input => input.checked && !input.disabled) }
  row(event) {
    if (event.target.closest("a, button, input, details, label, select")) return
    const input = event.currentTarget.querySelector('input[type="checkbox"]')
    if (!input || input.disabled) return
    input.checked = !input.checked; this.selection()
  }
  selectAll(event) { this.choiceTargets.filter(input => !input.disabled).forEach(input => { input.checked = event.target.checked }); this.selection() }
  clear() { this.choiceTargets.forEach(input => { input.checked = false }); this.selection() }
  selection() {
    const selected = this.selected(), totals = {}
    selected.forEach(input => { totals[input.dataset.currency] = (totals[input.dataset.currency] || 0) + Number(input.dataset.amount) })
    this.barTarget.hidden = selected.length === 0
    this.selectedCountTarget.textContent = `${selected.length} selected`
    this.selectedTotalsTarget.textContent = Object.entries(totals).map(([currency, amount]) => this.money(amount, currency)).join(" · ")
    this.choiceTargets.forEach(input => input.closest("tr").classList.toggle("pp-selected", input.checked))
    if (this.hasAllTarget) {
      const available = this.choiceTargets.filter(input => !input.disabled)
      this.allTarget.disabled = available.length === 0
      this.allTarget.checked = available.length > 0 && selected.length === available.length
      this.allTarget.indeterminate = selected.length > 0 && selected.length < available.length
    }
  }
  message(text, error = false) { this.feedbackTarget.hidden = false; this.feedbackTarget.textContent = text; this.feedbackTarget.classList.toggle("error", error) }
  async request(url, body = null) {
    const response = await fetch(url, {method: body ? "POST" : "GET", credentials: "same-origin", signal: this.abort.signal,
      headers: {Accept: "application/json", "Content-Type": "application/json", "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')?.content || ""}, ...(body ? {body: JSON.stringify(body)} : {})})
    if (response.redirected) throw new Error("Your admin session expired. Sign in again before paying.")
    if (!response.headers.get("content-type")?.includes("application/json")) throw new Error("The request could not be confirmed. Refresh and check the payout status.")
    const result = await response.json()
    if (!response.ok) throw new Error(result.error || "The payout request could not be completed.")
    return result
  }
  async preview(event) {
    if (this.busy) return
    const button = event.currentTarget; button.disabled = true
    try {
      const ids = button.dataset.id ? [button.dataset.id] : this.selected().map(input => input.value)
      const result = await this.request(this.previewUrlValue, {...this.filtersValue, all: button.dataset.all === "true", ids})
      this.token = result.token
      this.summaryTarget.textContent = `${result.count} payouts${result.sandbox ? " · Sandbox test" : ""}. Confirm these amounts before sending.`
      this.totalsTarget.replaceChildren()
      result.totals.forEach(total => { const line = document.createElement("div"); line.textContent = `${total.count} ${total.currency} payouts · ${this.money(total.amount, total.currency)}`; this.totalsTarget.append(line) })
      this.skippedTarget.hidden = result.skipped === 0
      this.skippedTarget.textContent = `${result.skipped} skipped: ${result.skipped_reasons.join("; ")}`
      this.dialogTarget.showModal()
    } catch (error) { if (error.name !== "AbortError") this.message(error.message, true) }
    finally { button.disabled = false }
  }
  cancel() { if (!this.busy) this.dialogTarget.close() }
  async send() {
    if (this.busy) return
    this.busy = true; this.sendTarget.disabled = true; this.sendTarget.textContent = "Sending…"
    try {
      const result = await this.request(this.payUrlValue, {token: this.token})
      this.dialogTarget.close(); this.clear()
      this.message(result.failed.length ? result.failed.map(row => `Payout ${row.id}: ${row.reason}`).join(" · ") : "Payouts submitted. Waiting for PayPal confirmation.", result.failed.length > 0)
      if (result.ids.length) this.monitor(result, 0)
    } catch (error) {
      if (error.name !== "AbortError") {
        this.dialogTarget.close()
        this.message(`${error.message} Check status before sending again.`, true)
      }
    }
    finally { this.busy = false; this.sendTarget.disabled = false; this.sendTarget.textContent = "Send payouts" }
  }
  async refresh(event) {
    event.stopPropagation()
    const button = event.currentTarget; button.disabled = true
    try { this.monitor(await this.request(button.dataset.url, {}), 0) }
    catch (error) { if (error.name !== "AbortError") this.message(error.message, true) }
    finally { button.disabled = false }
  }
  async monitor(result, count) {
    try {
      const url = new URL(result.status_url, window.location.origin)
      result.ids.forEach(id => url.searchParams.append("ids[]", id))
      const response = await this.request(url.href)
      const paid = response.payouts.filter(row => row.status === "paid"), pending = response.payouts.filter(row => ["queued", "processing", "pending", "unknown"].includes(row.status))
      const failed = response.payouts.filter(row => ["failed", "review"].includes(row.status))
      const reasons = [...failed.map(row => row.message), ...result.failed.map(row => `Payout ${row.id}: ${row.reason}`)]
      const message = `${paid.length} confirmed${paid.some(row => row.sandbox) ? " in sandbox" : " by PayPal"}; ${pending.length} awaiting confirmation; ${failed.length + result.failed.length} need attention.${reasons.length ? " " + reasons.join(" ") : ""}`
      this.message(message, failed.length + result.failed.length > 0)
      if (pending.length && count < 12) { this.timer = setTimeout(() => this.monitor(result, count + 1), 5000); return }
      try { sessionStorage.setItem("paypal-payout-result", message) } catch (_) {}
      window.location.reload()
    } catch (error) { if (error.name !== "AbortError") this.message(error.message, true) }
  }
}
